import styled from "@emotion/styled";

import UIcon from "lib/icons/untitled_ui/UIcon";

type SignalMatchCountProps = {
  count: number | undefined;
  isLoadingCounts: boolean;
};

/**
 * Shared match-count decoration for a signal-settings chip. Shows the count
 * whenever we have one — including an explicit (0), flagged with a warning
 * triangle — a spinner while a count is still in flight, and nothing when there
 * is no count and none is loading. Used by the keyword and CPV inputs so their
 * count/warning treatment stays identical.
 */
export default function SignalMatchCount({ count, isLoadingCounts }: SignalMatchCountProps) {
  const hasCount = count !== undefined;
  const isZero = count === 0;
  const showSpinner = !hasCount && isLoadingCounts;

  if (!hasCount && !showSpinner) {
    return null;
  }

  return (
    <CountLabel>
      {hasCount && `(${count})`}
      {isZero && (
        <IconSlot aria-label="No matches in the last 6 months">
          <UIcon icon="alertTriangle" size={14} />
        </IconSlot>
      )}
      {showSpinner && (
        <IconSlot role="status" aria-label="Loading match count">
          <UIcon icon="spinner" size={14} />
        </IconSlot>
      )}
    </CountLabel>
  );
}

const CountLabel = styled.span({
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
});

// Keeps the spinner / warning triangle vertically centred with the count text
// and gives the loading and loaded states the same footprint (no size jump).
const IconSlot = styled.span({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 14,
  height: 14,
});
